/**
 * Incident detail — Overview, Risk Assessment, Mitigation, Notifications, Sanctions, Education, Interviews, Documents, Assignments, Approval.
 */

import React, { useState, useEffect, useRef } from 'react';
import RiskAssessmentForm from './RiskAssessmentForm';
import JurisdictionsSection from './JurisdictionsSection';
import WorkflowBar from './WorkflowBar';
import NotificationTemplateLibrary from './NotificationTemplateLibrary';
import AffectedIndividuals from './AffectedIndividuals';
import PrivacyWarRoom from './PrivacyWarRoom';
import SpecialistPanel from './SpecialistPanel';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import { exportFullCaseToWord } from './exportPrivacyWord';
import { exportPrivacyCasePdf } from './exportPrivacyCasePdf';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_LABELS = {
  draft: 'Draft',
  under_investigation: 'Under investigation',
  risk_assessment_complete: 'Risk assessment complete',
  determination_pending_approval: 'Pending approval',
  approved_not_breach: 'Not a breach',
  approved_breach: 'Breach',
  notification_complete: 'Notification complete',
  closed: 'Closed'
};

const TABS = [
  'overview',
  'specialist',
  'risk',
  'jurisdictions',
  'mitigation',
  'notifications',
  'sanctions',
  'education',
  'interviews',
  'documents',
  'approval',
  'audit'
];

// PSI-aligned status pills — colored by lifecycle stage.
const STATUS_PILL = {
  draft: 'bg-slate-100 text-slate-600 border-slate-200',
  under_investigation: 'bg-blue-50 text-blue-700 border-blue-200',
  risk_assessment_complete: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  determination_pending_approval: 'bg-amber-50 text-amber-700 border-amber-200',
  approved_not_breach: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  approved_breach: 'bg-red-50 text-red-700 border-red-200',
  notification_complete: 'bg-teal-50 text-teal-700 border-teal-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};
const statusPill = (s) => `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_PILL[s] || 'bg-slate-100 text-slate-600 border-slate-200'}`;

// Human-friendly labels for the tab bar (used when the raw tab key isn't
// capitalized nicely by the default label logic).
const TAB_LABELS = {
  overview: 'Case Overview',
  specialist: 'Specialist',
  jurisdictions: 'Jurisdictions'
};

export default function PrivacyIncidentDetail({ incidentId, onBack, onRefresh }) {
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setIncident(data.incident);
      else setError(data.message || 'Failed to load');
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [incidentId]);

  const isClosed = incident?.status === 'closed';
  const canEdit = !isClosed;

  const patchIncident = async (payload) => {
    const r = await fetch(`${API}/api/privacy/incidents/${incidentId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
    });
    return r.json().catch(() => ({}));
  };
  const withMissing = (d, fallback) => {
    const base = d.message || fallback;
    return (Array.isArray(d.missing_prerequisites) && d.missing_prerequisites.length > 0)
      ? `${base} — ${d.missing_prerequisites.join(' · ')}` : base;
  };

  const handlePatch = async (body) => {
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      // Approval outcomes ("Not a breach" / "Breach") cannot be set by a raw
      // status PATCH — the backend requires the approve endpoint so the
      // compliance-lead role and the separation-of-duties / single-officer
      // setting are enforced and audited. So if the officer chooses one here,
      // do the whole thing: save any other edits, advance the case to pending
      // approval through the allowed steps, then approve.
      if (body && (body.status === 'approved_not_breach' || body.status === 'approved_breach')) {
        const determination = body.status;
        const { status, ...rest } = body;
        if (Object.keys(rest).length > 0) {
          const d = await patchIncident(rest);
          if (d.success && d.incident) setIncident(d.incident);
        }
        const cur = (incident && incident.status) || 'under_investigation';
        const path = [];
        if (cur === 'draft' || cur === 'under_investigation') path.push('risk_assessment_complete');
        if (cur !== 'determination_pending_approval') path.push('determination_pending_approval');
        for (const target of path) {
          const d = await patchIncident({ status: target });
          if (!d.success) { setError(withMissing(d, 'Could not move the case to pending approval')); setSaving(false); return; }
          if (d.incident) setIncident(d.incident);
        }
        const ar = await fetch(`${API}/api/privacy/incidents/${incidentId}/approve`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ determination_approved: determination })
        });
        const ad = await ar.json().catch(() => ({}));
        if (ar.ok && ad.success) { onRefresh?.(); await load(); }
        else { setError(withMissing(ad, 'Approval failed')); }
        setSaving(false);
        return;
      }

      const data = await patchIncident(body);
      if (data.success) {
        setIncident(data.incident);
        onRefresh?.();
      } else {
        setError(withMissing(data, 'Update failed'));
      }
    } catch (e) {
      setError(e.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  // Only the first load takes the page over. Later reloads (a save, a new
  // assessment version landing) refresh in place, so the open tab and the
  // form the officer is looking at never disappear.
  if (loading && !incident) return <div className="p-8 text-slate-500">Loading incident...</div>;
  if (error && !incident) return <div className="p-8 text-red-600">{error} <button type="button" onClick={onBack} className="ml-2 underline">Back</button></div>;
  if (!incident) return null;

  // The notification clock runs from the date the breach was known or should
  // have been known (breach_discovered_date, 45 CFR 164.404(a)(2)); the
  // determination date is only a legacy fallback. The deadline is the earliest
  // jurisdictional deadline if computed, otherwise the federal 60-day floor.
  const clockAnchor = incident.breach_discovered_date || incident.breach_determined_date || null;
  const deadlineDate = clockAnchor
    ? (incident.jurisdictional_obligations?.earliest_deadline_date
        || incident.notification_deadline_date
        || new Date(new Date(clockAnchor).getTime() + 60 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10))
    : null;
  const daysToDeadline = deadlineDate
    ? Math.ceil((new Date(deadlineDate) - new Date()) / (24 * 60 * 60 * 1000))
    : null;
  // When jurisdictional obligations exist, the earliest-deadline source is
  // typically a state-specific clock (e.g. "California AG 15d") — surface it
  // on the header so the compliance officer sees exactly which clock drives
  // the incident, not just the date.
  const earliestSource = incident.jurisdictional_obligations?.earliest_deadline_source;
  const hasMultiState = (incident.affected_states?.length || 0) > 0;

  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto">
      <button type="button" onClick={onBack} className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 transition-colors">
        ← Back to incidents
      </button>

      {/* PSI-aligned header card */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-blue-50/60 shadow-sm mb-6">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-600 to-amber-500" />
        <div className="p-6 sm:p-7 flex items-start justify-between flex-wrap gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {incident.id && <span className="text-[11px] font-mono text-slate-400">#{String(incident.id).slice(0, 8)}</span>}
              <span className={statusPill(incident.status)}>{STATUS_LABELS[incident.status] || incident.status}</span>
              {daysToDeadline != null && incident.status !== 'closed' && (
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    daysToDeadline < 0 ? 'bg-red-50 text-red-700 border-red-200'
                    : daysToDeadline <= 14 ? 'bg-amber-50 text-amber-700 border-amber-200'
                    : 'bg-slate-50 text-slate-600 border-slate-200'
                  }`}
                  title={earliestSource ? `Driver: ${earliestSource}` : 'HIPAA 60-day federal floor (from the breach-discovered date, 45 CFR 164.404(a)(2))'}
                >
                  {daysToDeadline < 0 ? `${Math.abs(daysToDeadline)} days over deadline` : `${daysToDeadline} days to deadline`}
                  {hasMultiState && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 uppercase tracking-wider">multi-state</span>
                  )}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              {incident.title || 'Untitled'}
            </h1>
          </div>
          <button
            type="button"
            onClick={() => exportOcrReport(incident)}
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 font-medium hover:bg-slate-50 hover:border-slate-300 text-sm shadow-sm transition-all"
          >
            Export breach report
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm">{error}</div>}

      <PhiFriendlyNote variant="minimum-necessary" className="mb-4" />

      {/* Workflow stepper + transition actions (Module 2 — state machine gating) */}
      <div className="mb-5">
        <WorkflowBar incident={incident} incidentId={incidentId} onRefresh={load} />
      </div>

      <div className="flex gap-1 overflow-x-auto pb-2 mb-5 p-1.5 bg-slate-100/70 border border-slate-200/60 rounded-2xl w-fit flex-wrap">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
              tab === t ? 'bg-gradient-to-r from-orange-600 to-amber-500 text-white shadow-md shadow-blue-600/20' : 'text-slate-500 hover:text-slate-800 hover:bg-white/70'
            }`}
          >
            {TAB_LABELS[t] || t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-5">
          {/* The record — case facts */}
          <OverviewSection incident={incident} canEdit={canEdit} saving={saving} onPatch={handlePatch} />
          {/* The live work — dates, briefing, parties, workstreams, actions, updates */}
          <PrivacyWarRoom incident={incident} incidentId={incidentId} onRefresh={load} canEdit={canEdit} />
        </div>
      )}
      {tab === 'specialist' && (
        <SpecialistPanel incidentId={incidentId} />
      )}
      {tab === 'risk' && (
        <RiskAssessmentForm incident={incident} incidentId={incidentId} onSave={load} canEdit={canEdit} />
      )}
      {tab === 'jurisdictions' && (
        <JurisdictionsSection incident={incident} incidentId={incidentId} canEdit={canEdit} onRefresh={load} />
      )}
      {tab === 'mitigation' && (
        <MitigationSection incident={incident} incidentId={incidentId} onRefresh={load} canEdit={canEdit} />
      )}
      {tab === 'notifications' && (
        <div className="space-y-6">
          <AffectedIndividuals incidentId={incidentId} canEdit={canEdit} />
          <NotificationsSection incident={incident} incidentId={incidentId} onRefresh={load} canEdit={canEdit} />
        </div>
      )}
      {tab === 'sanctions' && (
        <SanctionsSection incident={incident} incidentId={incidentId} onRefresh={load} canEdit={canEdit} />
      )}
      {tab === 'education' && (
        <EducationSection incident={incident} incidentId={incidentId} onRefresh={load} canEdit={canEdit} />
      )}
      {tab === 'interviews' && (
        <InterviewsSection incident={incident} incidentId={incidentId} onRefresh={load} canEdit={canEdit} />
      )}
      {tab === 'documents' && (
        <DocumentsSection incident={incident} incidentId={incidentId} onRefresh={load} canEdit={canEdit} />
      )}
      {tab === 'approval' && (
        <ApprovalSection incident={incident} incidentId={incidentId} onRefresh={load} onPatch={handlePatch} />
      )}
      {tab === 'audit' && (
        <AuditSection incidentId={incidentId} />
      )}
    </div>
  );
}

function exportOcrReport(incident) {
  const report = {
    title: incident.title,
    discovery_date: incident.discovery_date,
    breach_date: incident.breach_date,
    number_affected: incident.number_affected,
    covered_entity_details: incident.covered_entity_details,
    business_associate_involved: incident.business_associate_involved,
    business_associate_details: incident.business_associate_details,
    type_of_breach: incident.type_of_breach,
    location_of_breach: incident.location_of_breach,
    type_of_phi: incident.type_of_phi,
    safeguards_in_place: incident.safeguards_in_place,
    date_notice_sent: incident.date_notice_sent,
    media_notice_required: incident.media_notice_required,
    media_notice_date: incident.media_notice_date,
    substitute_notice_required: incident.substitute_notice_required,
    substitute_notice_date: incident.substitute_notice_date,
    actions_taken: incident.actions_taken,
    initial_or_addendum: incident.initial_or_addendum,
    status: incident.status,
    exported_at: new Date().toISOString()
  };
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `privacy-ocr-${incident.id || 'report'}-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function OverviewSection({ incident, canEdit, saving, onPatch }) {
  const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

  const [form, setForm] = useState({
    title: incident.title || '',
    summary: incident.summary || '',
    status: incident.status || 'draft',
    facts: incident.facts || '',
    who_impacted: incident.who_impacted || '',
    phi_involved: incident.phi_involved || '',
    root_cause_category: incident.root_cause_category || '',
    root_cause_notes: incident.root_cause_notes || '',
    department_name: incident.department_name || '',
    number_affected: incident.number_affected ?? '',
    business_associate_involved: incident.business_associate_involved || false
  });

  useEffect(() => {
    setForm({
      title: incident.title || '',
      summary: incident.summary || '',
      status: incident.status || 'draft',
      discovery_date: toDateInput(incident.discovery_date),
      state_deadline_date: toDateInput(incident.state_deadline_date),
      state_deadline_notes: incident.state_deadline_notes || '',
      facts: incident.facts || '',
      who_impacted: incident.who_impacted || '',
      phi_involved: incident.phi_involved || '',
      root_cause_category: incident.root_cause_category || '',
      root_cause_notes: incident.root_cause_notes || '',
      department_name: incident.department_name || '',
      number_affected: incident.number_affected ?? '',
      business_associate_involved: incident.business_associate_involved || false
    });
  }, [incident]);

  const handleSave = () => {
    const payload = {
      ...form,
      number_affected: form.number_affected === '' ? null : parseInt(form.number_affected, 10),
      root_cause_category: form.root_cause_category === '' ? null : form.root_cause_category
    };
    if (isNaN(payload.number_affected)) payload.number_affected = null;
    onPatch(payload);
  };

  // Upload how the case arrived (notification email, vendor breach notice, a PDF).
  // An AI extraction call (single Azure OpenAI completion — NOT the tool-using
  // assessor agent) reads it and fills the overview fields below for the officer
  // to review and Save. The file is attached to the case.
  const fileRef = useRef(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadNote, setUploadNote] = useState('');

  const runIntake = async (file) => {
    setUploadError(''); setUploadNote(''); setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/privacy/incidents/${incident.id}/intake-upload`, { method: 'POST', credentials: 'include', body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) { setUploadError(data.message || 'Could not read the file.'); return; }
      if (data.readable === false) {
        setUploadError(data.read_warning || 'The file was attached, but no readable text could be extracted, so the privacy agent cannot see its content.');
        return;
      }
      const f = data.fields || {};
      const next = {};
      const filled = [];
      const set = (k, v, label) => { if (v != null && String(v).trim() !== '') { next[k] = v; filled.push(label); } };
      set('title', f.title, 'title');
      set('summary', f.summary, 'summary');
      set('facts', f.what_happened, 'what happened');
      if (Array.isArray(f.phi_types) && f.phi_types.length) { next.phi_involved = f.phi_types.join(', '); filled.push('information involved'); }
      set('who_impacted', f.who_impacted, 'who was affected');
      if (f.number_affected != null && String(f.number_affected).trim() !== '' && !Number.isNaN(Number(f.number_affected))) { next.number_affected = String(f.number_affected); filled.push('number affected'); }
      set('root_cause_notes', f.suspected_root_cause, 'suspected cause');
      setForm((prev) => ({ ...prev, ...next }));
      const fname = data.file_name || file.name;
      setUploadNote(filled.length
        ? `Read “${fname}”. AI extracted: ${filled.join(', ')}. Review the fields below, then click Save changes to keep them.`
        : `Attached “${fname}” to the case, but couldn’t confidently extract structured fields from it.`);
    } catch (err) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploadBusy(false);
    }
  };

  const handleIntakeFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) await runIntake(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  const statusOptions = [
    'draft', 'under_investigation', 'risk_assessment_complete', 'determination_pending_approval',
    'approved_not_breach', 'approved_breach', 'notification_complete', 'closed'
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-slate-900 tracking-tight">Case overview</h3>
        <div className="flex items-center gap-2">
          {canEdit && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".eml,.msg,.pdf,.txt,.html,.htm,.docx,.doc,.xlsx,.xls,.csv,message/rfc822,application/pdf,text/plain,text/html,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={handleIntakeFile}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={uploadBusy}
                title="Upload how the case arrived — a notification email, a vendor breach notice, or a PDF. AI reads it (a single extraction pass, not the assessor agent) and fills the fields below for you to review."
                className="px-4 py-2 rounded-xl bg-slate-900 text-white font-medium text-sm hover:bg-slate-800 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                {uploadBusy && <span className="inline-block h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                {uploadBusy ? 'Reading…' : 'Upload source & read with AI'}
              </button>
            </>
          )}
          <button type="button" onClick={() => exportFullCaseToWord(incident)} title="Download the full case file — overview, risk assessment, accepted mitigation actions, notifications, sanctions, and education — as a modern Word document. Blank sections show N/A." className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors">
            Download case (Word)
          </button>
          <button type="button" onClick={() => exportPrivacyCasePdf(incident)} title="Download the case file as a regulator-ready PDF — overview, four-factor risk assessment, determination, jurisdictions, mitigation, notifications, sanctions, and education, in your organization's own colors. Internal work product stays internal." className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-medium text-sm hover:bg-slate-50 transition-colors">
            Download case (PDF)
          </button>
        </div>
      </div>
      {uploadError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{uploadError}</div>
      )}
      {uploadNote && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">{uploadNote}</div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Case status</label>
          <select
            value={form.status}
            onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            disabled={!canEdit}
            className="w-full rounded-xl border border-slate-200 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s] || s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Title</label>
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            disabled={!canEdit}
            className="w-full rounded-xl border border-slate-200 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-600 mb-1">Brief summary of the incident</label>
          <textarea
            value={form.summary}
            onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
            disabled={!canEdit}
            rows={2}
            className="w-full rounded-xl border border-slate-200 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-slate-600 mb-1">What happened? (who was involved, what occurred, when, where, and why)</label>
          <textarea
            value={form.facts}
            onChange={(e) => setForm((f) => ({ ...f, facts: e.target.value }))}
            disabled={!canEdit}
            placeholder="Describe the incident in plain language for the case file."
            rows={3}
            className="w-full rounded-xl border border-slate-200 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">Who was affected (e.g. patients, members)</label>
          <input
            value={form.who_impacted}
            onChange={(e) => setForm((f) => ({ ...f, who_impacted: e.target.value }))}
            disabled={!canEdit}
            className="w-full rounded-xl border border-slate-200 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">What type of health information was involved?</label>
          <input
            value={form.phi_involved}
            onChange={(e) => setForm((f) => ({ ...f, phi_involved: e.target.value }))}
            disabled={!canEdit}
            className="w-full rounded-xl border border-slate-200 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1">How many people were affected? (for breach reporting)</label>
          <input
            type="number"
            min={0}
            value={form.number_affected}
            onChange={(e) => setForm((f) => ({ ...f, number_affected: e.target.value }))}
            disabled={!canEdit}
            placeholder="Leave blank if unknown"
            className="w-full rounded-xl border border-slate-200 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-slate-900 disabled:bg-slate-50"
          />
        </div>
      </div>
      {canEdit && (
        <button type="button" onClick={handleSave} disabled={saving} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-all">
          {saving ? 'Saving...' : 'Save overview'}
        </button>
      )}
    </div>
  );
}

const CONF_TONE = { high: 'bg-emerald-50 text-emerald-700 ring-emerald-200', medium: 'bg-amber-50 text-amber-700 ring-amber-200', low: 'bg-slate-100 text-slate-600 ring-slate-200' };
const STATUS_TONE = { completed: 'bg-emerald-50 text-emerald-700', in_progress: 'bg-blue-50 text-blue-700', planned: 'bg-violet-50 text-violet-700', unknown: 'bg-slate-100 text-slate-600' };

// A single proposed mitigation, with its source trail, editable before accepting.
function ProposalCard({ proposal, incidentId, onResolved }) {
  const [title, setTitle] = useState(proposal.title || '');
  const [description, setDescription] = useState(proposal.description || '');
  const [actionType, setActionType] = useState(proposal.suggested_action_type || '');
  const [dueDate, setDueDate] = useState(proposal.suggested_due_date ? String(proposal.suggested_due_date).slice(0, 10) : '');
  const [actionDate, setActionDate] = useState(proposal.source_date ? String(proposal.source_date).slice(0, 10) : '');
  const [busy, setBusy] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const refs = Array.isArray(proposal.source_refs) ? proposal.source_refs : [];

  const accept = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/privacy/incidents/${incidentId}/mitigation-proposals/${proposal.id}/accept`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim() || proposal.title, description: description.trim() || null, action_type: actionType || null, due_date: dueDate || null, action_date: actionDate || null })
      });
      const d = await r.json();
      if (d.success) onResolved(proposal.id, 'accepted'); else { alert(d.message || 'Could not add'); setBusy(false); }
    } catch { alert('Request failed'); setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt('Optional: why is this not a mitigation action? This helps the assistant get better.');
    if (reason === null) { /* cancelled the prompt, treat as no-reason reject */ }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/privacy/incidents/${incidentId}/mitigation-proposals/${proposal.id}/reject`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || null })
      });
      const d = await r.json();
      if (d.success) onResolved(proposal.id, 'rejected'); else { alert(d.message || 'Could not reject'); setBusy(false); }
    } catch { alert('Request failed'); setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} className="flex-1 font-medium text-slate-900 bg-transparent border-b border-transparent hover:border-slate-200 focus:border-blue-400 focus:outline-none py-0.5" />
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {proposal.confidence && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${CONF_TONE[proposal.confidence] || CONF_TONE.medium}`}>{proposal.confidence} confidence</span>}
          {proposal.status_observed && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_TONE[proposal.status_observed] || STATUS_TONE.unknown}`}>{String(proposal.status_observed).replace('_', ' ')}</span>}
        </div>
      </div>
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full text-sm text-slate-600 rounded-lg border border-slate-200 px-2.5 py-1.5 resize-y mb-2 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <input value={actionType} onChange={(e) => setActionType(e.target.value)} placeholder="Type (e.g. containment)" className="text-xs rounded-lg border border-slate-200 px-2 py-1 w-44" />
        <label className="text-[11px] text-slate-500 inline-flex items-center gap-1">Date <input type="date" value={actionDate} onChange={(e) => setActionDate(e.target.value)} title="Date of the source document or note this action came from" className="text-xs rounded-lg border border-slate-200 px-2 py-1" /></label>
        <label className="text-[11px] text-slate-500 inline-flex items-center gap-1">Due <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="text-xs rounded-lg border border-slate-200 px-2 py-1" /></label>
        {proposal.duplicate_of_mitigation_id && <span className="text-[11px] text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">May already be recorded</span>}
      </div>
      {refs.length > 0 && (
        <div className="mb-3">
          <button type="button" onClick={() => setShowSource((s) => !s)} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
            {showSource ? 'Hide' : 'Show'} where this came from ({refs.length})
          </button>
          {showSource && (
            <ul className="mt-2 space-y-1.5">
              {refs.map((r, i) => (
                <li key={i} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-2">
                  <span className="font-medium text-slate-500">{({ document: '📄', update: '🗒', action: '✅', workstream: '🧭' }[r.kind]) || '🗒'} {r.label}:</span>{' '}
                  <span className="italic">“{r.quote}”</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" onClick={accept} disabled={busy} className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">{busy ? '…' : 'Add to mitigations'}</button>
        <button type="button" onClick={reject} disabled={busy} className="px-4 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">Not a mitigation</button>
      </div>
    </div>
  );
}

function MitigationSection({ incident, incidentId, onRefresh, canEdit }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [actionDate, setActionDate] = useState('');
  const [adding, setAdding] = useState(false);
  const mitigations = incident.mitigations || [];

  const [proposals, setProposals] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanSummary, setScanSummary] = useState('');
  const [scanInfo, setScanInfo] = useState(null);
  const [scanError, setScanError] = useState('');

  // Freeform feedback the mitigation agent learns from (mirrors the risk
  // assessment reviewer-note box). Stored as an officer correction and applied
  // on the next scan, the same way rejections are.
  const [fbText, setFbText] = useState('');
  const [fbBusy, setFbBusy] = useState(false);
  const [fbMsg, setFbMsg] = useState('');
  const sendMitFeedback = async () => {
    if (!fbText.trim()) return;
    setFbBusy(true); setFbMsg('');
    try {
      const r = await fetch(`${API}/api/privacy/incidents/${incidentId}/ai/feedback`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent: 'privacy_mitigation_harvester', field: 'mitigation_extraction', corrected_value: fbText.trim(), reason: 'Officer guidance on mitigation extraction' }),
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) { setFbText(''); setFbMsg('Saved. The agent will apply this the next time you scan.'); }
      else setFbMsg(d.message || 'Could not save the feedback. Please try again.');
    } catch { setFbMsg('Could not save the feedback. Please try again.'); } finally { setFbBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/privacy/incidents/${incidentId}/mitigation-proposals`, { credentials: 'include' })
      .then((r) => r.json()).then((d) => { if (alive && d.success) setProposals(d.proposals || []); }).catch(() => {});
    return () => { alive = false; };
  }, [incidentId]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/mitigations`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), description: description.trim() || null, due_date: dueDate || null, action_date: actionDate || null })
      });
      if (res.ok) { setTitle(''); setDescription(''); setDueDate(''); setActionDate(''); onRefresh(); }
    } finally { setAdding(false); }
  };

  const runScan = async () => {
    setScanning(true); setScanError(''); setScanSummary('');
    try {
      const r = await fetch(`${API}/api/privacy/incidents/${incidentId}/mitigations/harvest`, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (d.success) { setProposals(d.proposals || []); setScanSummary(d.summary || ''); setScanInfo(d.agent || null); }
      else setScanError(d.message || 'The scan could not finish. Please try again.');
    } catch { setScanError('The scan could not finish. Please try again.'); } finally { setScanning(false); }
  };

  const onResolved = (pid) => { setProposals((prev) => prev.filter((p) => p.id !== pid)); onRefresh(); };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-4">Mitigation actions</h3>
        {mitigations.length === 0 && !canEdit && <p className="text-slate-500 text-sm">None recorded.</p>}
        {mitigations.length === 0 && canEdit && <p className="text-slate-500 text-sm mb-4">None recorded yet. Add them by hand below, or scan the case to find the ones already described in the record.</p>}
        {mitigations.length > 0 && (
          <ul className="space-y-2 mb-4">
            {mitigations.map((m) => (
              <li key={m.id} className="flex justify-between items-start p-3 rounded-xl bg-slate-50">
                <div>
                  <p className="font-medium text-slate-900">{m.title}</p>
                  {m.description && <p className="text-sm text-slate-600">{m.description}</p>}
                  <p className="text-xs text-slate-500">
                    {m.action_date && <span>Date: {String(m.action_date).slice(0, 10)}</span>}
                    {m.action_date && m.due_date && <span> · </span>}
                    {m.due_date && <span>Due: {String(m.due_date).slice(0, 10)}</span>}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <form onSubmit={add} className="flex flex-wrap gap-3 items-end">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Action title" className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" required />
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
            <label className="text-xs text-slate-500 flex flex-col gap-0.5">Date<input type="date" value={actionDate} onChange={(e) => setActionDate(e.target.value)} className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" /></label>
            <label className="text-xs text-slate-500 flex flex-col gap-0.5">Due<input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" /></label>
            <button type="submit" disabled={adding} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-colors">Add</button>
          </form>
        )}
      </div>

      {canEdit && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h3 className="font-semibold text-slate-900">Find mitigation actions in the case record</h3>
              <p className="text-slate-500 text-sm mt-0.5 max-w-2xl">Scan the case updates and documents for mitigation actions that were taken or planned, then review and add the ones that belong. Add forensic reports and meeting files in the War Room on the Case Overview tab first, and the scan will read them.</p>
            </div>
            <button type="button" onClick={runScan} disabled={scanning} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-colors flex-shrink-0">
              {scanning ? 'Reading the case…' : 'Scan case for mitigation actions'}
            </button>
          </div>

          {scanError && <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{scanError}</div>}
          {scanInfo && !scanError && (
            <p className="mt-4 text-xs text-slate-400">
              Reviewed {scanInfo.sources?.updates ?? 0} update{scanInfo.sources?.updates === 1 ? '' : 's'} and {scanInfo.sources?.documents ?? 0} document{scanInfo.sources?.documents === 1 ? '' : 's'}
              {scanInfo.second_pass ? ', with a second pass to cover everything' : ''}. Proposed {scanInfo.kept_after_grounding ?? 0}, each backed by a quote from the record.
            </p>
          )}
          {scanSummary && <p className="mt-2 text-sm text-slate-600">{scanSummary}</p>}

          {proposals.length > 0 && (
            <div className="mt-5">
              <p className="text-sm font-medium text-slate-700 mb-3">{proposals.length} proposed action{proposals.length === 1 ? '' : 's'} to review</p>
              <div className="space-y-3">
                {proposals.map((p) => <ProposalCard key={p.id} proposal={p} incidentId={incidentId} onResolved={onResolved} />)}
              </div>
            </div>
          )}
          {proposals.length === 0 && scanInfo && !scanError && (
            <p className="mt-4 text-sm text-slate-500">No new mitigation actions were found in the record beyond what is already recorded.</p>
          )}

          <div className="mt-6 pt-5 border-t border-slate-100">
            <h4 className="font-semibold text-slate-900 text-sm">Teach the mitigation agent</h4>
            <p className="text-slate-500 text-sm mt-0.5 max-w-2xl">Tell the agent how to do better: what kinds of items to stop proposing, what it tends to miss, or how you want actions worded. Your guidance, and every action you mark "Not a mitigation," are remembered and applied on the next scan.</p>
            <textarea value={fbText} onChange={(e) => setFbText(e.target.value)} rows={3} placeholder="Write your guidance in your own words…" className="mt-3 w-full rounded-xl border border-slate-200/80 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm" />
            <div className="mt-2 flex items-center gap-3">
              <button type="button" onClick={sendMitFeedback} disabled={fbBusy || !fbText.trim()} className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-medium text-sm hover:bg-slate-50 disabled:opacity-50">{fbBusy ? 'Saving…' : 'Teach the agent'}</button>
              {fbMsg && <span className="text-xs text-slate-500">{fbMsg}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationsSection({ incident, incidentId, onRefresh, canEdit }) {
  const [notificationType, setNotificationType] = useState('individual');
  const [sentDate, setSentDate] = useState('');
  const [method, setMethod] = useState('');
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);
  const list = incident.notifications || [];

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/notifications`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_type: notificationType, sent_date: sentDate || null, method: method.trim() || null, notes: notes.trim() || null })
      });
      if (res.ok) {
        setSentDate('');
        setMethod('');
        setNotes('');
        onRefresh();
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Template library — generate HIPAA-compliant notice letters */}
      <NotificationTemplateLibrary incident={incident} />

      {/* Disclaimer */}
      <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-sm text-amber-900">
        <p className="font-semibold mb-1">When notification may not be federally required</p>
        <p>If a HIPAA exception applies (good-faith workforce acquisition, inadvertent disclosure to an authorized person, or a recipient who could not reasonably retain the information), <strong>federal breach notification to HHS/OCR is not required</strong>. State laws still apply independently — notification to state regulators or to affected individuals may still be required under state law. Verify with counsel before concluding no notice is needed.</p>
      </div>

      {/* Log of sent notifications */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-slate-900">Notifications sent</h3>
            <p className="text-xs text-slate-500 mt-0.5">Record every notification you issue — individual, media, substitute, or OCR portal submission.</p>
          </div>
          <span className="inline-flex items-center justify-center min-w-[36px] h-7 px-2 rounded-full bg-slate-100 text-slate-700 text-xs font-bold">
            {list.length}
          </span>
        </div>

        {list.length === 0 && !canEdit && <p className="text-slate-400 text-sm italic">None recorded.</p>}

        {list.length > 0 && (
          <ul className="space-y-2 mb-5">
            {list.map((n) => (
              <li key={n.id} className="flex items-start gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-100">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center text-[10px] font-bold uppercase tracking-wider">
                  {(n.notification_type || '?').slice(0, 3)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-semibold text-slate-900 capitalize">{(n.notification_type || '').replace(/_/g, ' ')}</span>
                    {n.sent_date && <span className="text-xs text-slate-500">Sent {n.sent_date}</span>}
                    {n.method && <span className="text-xs text-slate-500">· via {n.method}</span>}
                  </div>
                  {n.notes && <p className="text-sm text-slate-600 mt-1">{n.notes}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}

        {canEdit && (
          <form onSubmit={add} className="border-t border-slate-100 pt-5 space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Record a notification</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Type</label>
                <select
                  value={notificationType}
                  onChange={(e) => setNotificationType(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                  <option value="individual">Individual</option>
                  <option value="media">Media</option>
                  <option value="substitute">Substitute</option>
                  <option value="ocr_submission">Federal (HHS/OCR portal)</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Sent date</label>
                <input
                  type="date"
                  value={sentDate}
                  onChange={(e) => setSentDate(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Method</label>
                <input
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  placeholder="e.g. certified mail, email"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="e.g. 342 letters mailed via USPS certified return receipt"
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-slate-900 bg-white shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={adding}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-sm font-semibold shadow-md hover:shadow-lg hover:shadow-indigo-600/30 disabled:opacity-50 transition-all"
            >
              {adding ? 'Adding...' : 'Record notification'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function SanctionsSection({ incident, incidentId, onRefresh, canEdit }) {
  const [description, setDescription] = useState('');
  const [sanctionType, setSanctionType] = useState('');
  const [appliedAt, setAppliedAt] = useState('');
  const [adding, setAdding] = useState(false);
  const list = incident.sanctions || [];

  const add = async (e) => {
    e.preventDefault();
    if (!description.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/sanctions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: description.trim(), sanction_type: sanctionType.trim() || null, applied_at: appliedAt || null })
      });
      if (res.ok) {
        setDescription('');
        setSanctionType('');
        setAppliedAt('');
        onRefresh();
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-900 mb-4">Sanctions</h3>
      {list.length === 0 && !canEdit && <p className="text-slate-500 text-sm">None recorded.</p>}
      {list.length > 0 && (
        <ul className="space-y-2 mb-4">
          {list.map((s) => (
            <li key={s.id} className="p-3 rounded-xl bg-slate-50">
              <p className="text-slate-900">{s.description}</p>
              {s.sanction_type && <span className="text-sm text-slate-600">{s.sanction_type}</span>}
              {s.applied_at && <span className="text-sm text-slate-500 block">Applied: {s.applied_at}</span>}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form onSubmit={add} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Description *</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" required />
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Sanction type</label>
              <input value={sanctionType} onChange={(e) => setSanctionType(e.target.value)} placeholder="e.g. written warning" className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1">Applied date</label>
              <input type="date" value={appliedAt} onChange={(e) => setAppliedAt(e.target.value)} className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
            </div>
          </div>
          <button type="submit" disabled={adding} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-colors">Add sanction</button>
        </form>
      )}
    </div>
  );
}

function EducationSection({ incident, incidentId, onRefresh, canEdit }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [completedAt, setCompletedAt] = useState('');
  const [adding, setAdding] = useState(false);
  const list = incident.education_actions || [];

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/education`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), description: description.trim() || null, completed_at: completedAt || null })
      });
      if (res.ok) {
        setTitle('');
        setDescription('');
        setCompletedAt('');
        onRefresh();
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-900 mb-4">Education / prevention</h3>
      {list.length === 0 && !canEdit && <p className="text-slate-500 text-sm">None recorded.</p>}
      {list.length > 0 && (
        <ul className="space-y-2 mb-4">
          {list.map((e) => (
            <li key={e.id} className="p-3 rounded-xl bg-slate-50">
              <p className="font-medium text-slate-900">{e.title}</p>
              {e.description && <p className="text-sm text-slate-600">{e.description}</p>}
              {e.completed_at && <span className="text-sm text-slate-600">Completed: {e.completed_at}</span>}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form onSubmit={add} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. HIPAA refresher training" className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Completed date</label>
            <input type="date" value={completedAt} onChange={(e) => setCompletedAt(e.target.value)} className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
          </div>
          <button type="submit" disabled={adding} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-colors">Add education action</button>
        </form>
      )}
    </div>
  );
}

function InterviewsSection({ incident, incidentId, onRefresh, canEdit }) {
  const [interviewee, setInterviewee] = useState('');
  const [interviewDate, setInterviewDate] = useState('');
  const [summary, setSummary] = useState('');
  const [adding, setAdding] = useState(false);
  const list = incident.interviews || [];

  const add = async (e) => {
    e.preventDefault();
    if (!summary.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/interviews`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ interviewee_role_or_name: interviewee.trim() || null, interview_date: interviewDate || null, summary: summary.trim() })
      });
      if (res.ok) {
        setInterviewee('');
        setInterviewDate('');
        setSummary('');
        onRefresh();
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-900 mb-4">Investigation interviews</h3>
      {list.length === 0 && !canEdit && <p className="text-slate-500 text-sm">None recorded.</p>}
      {list.length > 0 && (
        <ul className="space-y-2 mb-4">
          {list.map((i) => (
            <li key={i.id} className="p-3 rounded-xl bg-slate-50">
              <p className="font-medium text-slate-900">{i.interviewee_role_or_name || 'Unknown'}</p>
              {i.interview_date && <span className="text-sm text-slate-600">Date: {i.interview_date}</span>}
              <p className="text-sm text-slate-700 mt-1">{i.summary}</p>
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form onSubmit={add} className="space-y-3">
          <input value={interviewee} onChange={(e) => setInterviewee(e.target.value)} placeholder="Interviewee (role or name)" className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
          <input type="date" value={interviewDate} onChange={(e) => setInterviewDate(e.target.value)} className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Summary *" rows={3} className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" required />
          <button type="submit" disabled={adding} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-colors">Add interview</button>
        </form>
      )}
    </div>
  );
}

const DOC_CATEGORIES = [
  { value: 'audit_log', label: 'Audit log or access record' },
  { value: 'forensic_report', label: 'Forensic or investigation report' },
  { value: 'ba_communication', label: 'Business associate letter or communication' },
  { value: 'ocr_submission_proof', label: 'Proof we reported to HHS (federal)' },
  { value: 'law_enforcement_delay', label: 'Law enforcement delay request' },
  { value: 'mitigation_evidence', label: 'Evidence of steps we took to fix it' },
  { value: 'other', label: 'Other' }
];

function DocumentsSection({ incident, incidentId, onRefresh, canEdit }) {
  const [category, setCategory] = useState('other');
  const [fileName, setFileName] = useState('');
  const [filePathOrUrl, setFilePathOrUrl] = useState('');
  const [adding, setAdding] = useState(false);
  const list = incident.documents || [];

  const add = async (e) => {
    e.preventDefault();
    if (!fileName.trim()) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/documents`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, file_name: fileName.trim(), file_path_or_url: filePathOrUrl.trim() || fileName.trim() })
      });
      if (res.ok) {
        setFileName('');
        setFilePathOrUrl('');
        onRefresh();
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-900 mb-4">Supporting documents</h3>
      <p className="text-slate-500 text-sm mb-4">Add references to documents that support this case (e.g. audit logs, investigation reports, proof of federal reporting). You can note where the file is stored or a link.</p>
      {list.length === 0 && !canEdit && <p className="text-slate-500 text-sm">No documents yet.</p>}
      {list.length > 0 && (
        <ul className="space-y-2 mb-4">
          {list.map((d) => (
            <li key={d.id} className="p-3 rounded-xl bg-slate-50 flex justify-between items-start">
              <div>
                <span className="font-medium text-slate-900">{d.file_name}</span>
                <span className="text-xs text-slate-500 ml-2">{DOC_CATEGORIES.find((c) => c.value === d.category)?.label || d.category}</span>
                {d.file_path_or_url && <p className="text-xs text-slate-500 mt-1 truncate max-w-md">{d.file_path_or_url}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <form onSubmit={add} className="space-y-3">
          <div className="flex flex-wrap gap-3">
            <div className="min-w-[180px]">
              <label className="block text-sm font-medium text-slate-600 mb-1">Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2">
                {DOC_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-600 mb-1">File name *</label>
              <input value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="e.g. audit-log-2024-01.pdf" className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" required />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">File path or URL (optional)</label>
            <input value={filePathOrUrl} onChange={(e) => setFilePathOrUrl(e.target.value)} placeholder="Storage path or URL to document" className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
          </div>
          <button type="submit" disabled={adding} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-colors">Add document</button>
        </form>
      )}
    </div>
  );
}

function AssignmentsSection({ incident, incidentId, onRefresh, canEdit }) {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [adding, setAdding] = useState(false);
  const assignments = incident.assignments || [];

  useEffect(() => {
    (async () => {
      const res = await fetch(`${API}/api/privacy/users`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setUsers(data.users || []);
    })();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!selectedUserId) return;
    setAdding(true);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/assignments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: selectedUserId })
      });
      if (res.ok) {
        setSelectedUserId('');
        onRefresh();
      }
    } finally {
      setAdding(false);
    }
  };

  const remove = async (userId) => {
    try {
      await fetch(`${API}/api/privacy/incidents/${incidentId}/assignments/${userId}`, { method: 'DELETE', credentials: 'include' });
      onRefresh();
    } catch (_) {}
  };

  const assignedIds = new Set(assignments.map((a) => a.user_id));
  const available = users.filter((u) => !assignedIds.has(u.id));

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-900 mb-4">Assignees (same org only)</h3>
      <p className="text-slate-500 text-sm mb-4">Assign Core users to this incident. They gain access only to this case.</p>
      {assignments.length > 0 && (
        <ul className="space-y-2 mb-4">
          {assignments.map((a) => (
            <li key={a.id} className="flex justify-between items-center p-3 rounded-xl bg-slate-50">
              <span className="font-medium text-slate-900">{a.user_name || a.user_email || a.user_id}</span>
              {canEdit && (
                <button type="button" onClick={() => remove(a.user_id)} className="text-red-600 text-sm hover:underline">Remove</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && available.length > 0 && (
        <form onSubmit={add} className="flex gap-2 items-end">
          <select value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2">
            <option value="">Select user...</option>
            {available.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email}</option>
            ))}
          </select>
          <button type="submit" disabled={adding || !selectedUserId} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-colors">Add</button>
        </form>
      )}
    </div>
  );
}

function ApprovalSection({ incident, incidentId, onRefresh, onPatch }) {
  const [determination, setDetermination] = useState('approved_not_breach');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [approvalError, setApprovalError] = useState(null);
  const [missingPrereqs, setMissingPrereqs] = useState([]);
  const [isSeparationBlock, setIsSeparationBlock] = useState(false);
  const approvals = incident.approvals || [];
  const canApprove = incident.status === 'determination_pending_approval';
  const canClose = incident.status !== 'closed' && ['approved_not_breach', 'approved_breach', 'notification_complete'].includes(incident.status);

  const submit = async (e) => {
    e.preventDefault();
    if (!canApprove) return;
    setSubmitting(true);
    setApprovalError(null);
    setMissingPrereqs([]);
    setIsSeparationBlock(false);
    try {
      const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/approve`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ determination_approved: determination, notes })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        onRefresh();
      } else {
        setApprovalError(data.message || `Approval failed (${res.status})`);
        if (Array.isArray(data.missing_prerequisites)) setMissingPrereqs(data.missing_prerequisites);
        if (data.error_code === 'separation_of_duties') setIsSeparationBlock(true);
      }
    } catch (err) {
      setApprovalError(err.message || 'Approval failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseCase = async () => {
    if (!canClose || !onPatch) return;
    if (!window.confirm('Close this case? The incident will be marked closed and no further edits will be allowed (except addenda).')) return;
    setClosing(true);
    try {
      await onPatch({
        status: 'closed',
        closed_at: new Date().toISOString().slice(0, 10)
      });
      onRefresh();
    } finally {
      setClosing(false);
    }
  };

  const [reopening, setReopening] = useState(false);
  const handleReopen = async () => {
    const reason = window.prompt('Reopening a closed case is recorded in the audit log. Enter the justification for reopening:');
    if (reason === null) return; // cancelled
    if (!reason.trim()) { window.alert('A justification is required to reopen the case.'); return; }
    setReopening(true);
    try {
      const r = await fetch(`${API}/api/privacy/incidents/${incidentId}/reopen`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: reason.trim() })
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.success) onRefresh();
      else window.alert(d.message || 'Could not reopen the case.');
    } finally {
      setReopening(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-900 mb-4">Privacy officer sign-off</h3>
      <p className="text-slate-500 text-sm mb-4">Only the designated privacy officer (or compliance lead) can approve the final decision. Once approved, the risk assessment is locked for the record. The same role should close the case when all follow-up is complete.</p>
      {approvals.length > 0 && (
        <ul className="space-y-2 mb-4">
          {approvals.map((a) => (
            <li key={a.id} className="p-3 rounded-xl bg-slate-50">
              <span className="font-medium text-slate-900">{a.determination_approved === 'approved_breach' ? 'Breach' : 'Not a breach'}</span>
              <span className="text-sm text-slate-600 ml-2">by {a.approver_name} at {a.approved_at}</span>
            </li>
          ))}
        </ul>
      )}
      {canApprove && (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Your decision</label>
            <select value={determination} onChange={(e) => setDetermination(e.target.value)} className="rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2">
              <option value="approved_not_breach">Not a reportable breach</option>
              <option value="approved_breach">Breach — notification required</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full rounded-xl border border-slate-200/80 shadow-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 px-3 py-2" />
          </div>

          {approvalError && (
            <div className={`rounded-xl p-4 border ${
              isSeparationBlock
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isSeparationBlock ? 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z' : 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'} />
                </svg>
                <div className="flex-1 text-sm">
                  <p className="font-semibold mb-1">
                    {isSeparationBlock ? 'Separation of duties required' : 'Approval blocked'}
                  </p>
                  <p>{approvalError}</p>
                  {missingPrereqs.length > 0 && (
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      {missingPrereqs.map((m, i) => (
                        <li key={i} className="text-xs">{m}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          <button type="submit" disabled={submitting} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition-colors">Approve</button>
        </form>
      )}
      {canClose && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <p className="text-slate-600 text-sm mb-2">When all required follow-up (e.g. notifications) is done, close the case to lock the record.</p>
          <button type="button" onClick={handleCloseCase} disabled={closing} className="px-5 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-medium shadow-sm hover:bg-slate-50 disabled:opacity-50 transition-colors">
            {closing ? 'Closing...' : 'Close case'}
          </button>
        </div>
      )}
      {incident.status === 'closed' && (
        <div className="mt-4 pt-4 border-t border-slate-200">
          <p className="text-slate-600 text-sm mb-2">This case is closed and locked. No edits are allowed unless it is reopened with a recorded justification.</p>
          <button type="button" onClick={handleReopen} disabled={reopening} className="px-5 py-2.5 rounded-xl border border-amber-300 text-amber-800 font-medium shadow-sm hover:bg-amber-50 disabled:opacity-50 transition-colors">
            {reopening ? 'Reopening...' : 'Reopen case (justification required)'}
          </button>
        </div>
      )}
    </div>
  );
}

function AuditSection({ incidentId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/privacy/incidents/${incidentId}/audit`, { credentials: 'include' });
        const data = await res.json();
        if (!cancelled && data.success) setLogs(data.logs || []);
      } catch (_) {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [incidentId]);

  if (loading) return <div className="bg-white rounded-2xl border border-slate-200 p-6 text-slate-500">Loading audit log...</div>;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6">
      <h3 className="font-semibold text-slate-900 mb-4">Audit log</h3>
      <p className="text-slate-500 text-sm mb-4">Append-only record of who did what and when. Immutable for defensibility.</p>
      {logs.length === 0 ? (
        <p className="text-slate-500 text-sm">No audit entries yet.</p>
      ) : (
        <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
          {logs.map((log) => (
            <li key={log.id} className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium text-slate-900">{log.action}</span>
                {log.entity_type && <span className="text-slate-500">{log.entity_type}</span>}
                <span className="text-slate-500">{log.user_name || log.user_email || log.user_id}</span>
                <span className="text-slate-400 text-xs">{log.created_at}</span>
              </div>
              {(log.after_values && Object.keys(log.after_values).length > 0) && (
                <pre className="mt-2 text-xs text-slate-600 overflow-x-auto bg-white p-2 rounded border border-slate-200/80">
                  {JSON.stringify(log.after_values, null, 2)}
                </pre>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
